import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { installConsoleErrorBuffer } from "@/lib/consoleErrorBuffer";

// Install the client-side error ring buffer as early as possible so
// even startup crashes are captured for the feedback pill.
installConsoleErrorBuffer();

// ── Crawler escape hatch ────────────────────────────────────────────
// On public, dynamic, SSR-able routes (`/c/*`, `/share/*`, `/p/*`)
// the deployment platform pre-renders the React tree server-side so
// crawlers receive a fully-populated HTML body. When React then
// `createRoot()`s on top of that DOM, it wipes the pre-rendered tree
// and re-renders client-side - which fires a `/api/public/...` fetch
// that can be intermittently blocked by Cloudflare's Bot Fight Mode.
// That left Googlebot looking at the React error UI ("Collection
// unavailable") and produced "Soft 404" / "Excluded by noindex"
// verdicts in Google Search Console.
//
// Skipping the React mount entirely for verified crawler user-agents
// on these three URL families lets the SSR'd HTML stand on its own.
// Real users still mount React as normal because they don't match
// the UA pattern. We also dismiss the brand splash so the SSR'd
// body is what the crawler sees.
const CRAWLER_UA_RE = /Googlebot|bingbot|Baiduspider|YandexBot|DuckDuckBot|Slurp|AdsBot-Google|Mediapartners-Google|Google-InspectionTool|facebookexternalhit|Twitterbot|LinkedInBot|WhatsApp|Slackbot|TelegramBot|Discordbot|Applebot/i;
const PUBLIC_SSR_ROUTE_RE = /^\/(c|share|p)\/[A-Za-z0-9_-]+\/?$/;

function shouldSkipReactMount() {
    try {
        const ua = navigator.userAgent || "";
        if (!CRAWLER_UA_RE.test(ua)) return false;
        const path = window.location.pathname || "/";
        if (!PUBLIC_SSR_ROUTE_RE.test(path)) return false;
        // Only skip if the platform actually pre-rendered something
        // meaningful (>1 child). Otherwise React must mount so the
        // page isn't blank.
        const root = document.getElementById("root");
        return !!(root && root.childElementCount > 0);
    } catch (_e) {
        return false;
    }
}

if (shouldSkipReactMount()) {
    // Drop the splash overlay so the pre-rendered body is visible.
    const splash = document.getElementById("brainsync-splash");
    if (splash) splash.remove();
} else {
    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
        <React.StrictMode>
            <App />
        </React.StrictMode>,
    );
}

// Fade the pre-React HTML splash out via a global helper so the final-
// state pages (Dashboard, AppLanding, etc.) can dismiss it themselves
// once they're ready to paint. This avoids the awkward two-stage flash
// (HTML splash → React loader → real content) when the dashboard loads
// quickly. Pages call window.__brainsyncDismissSplash() once mounted.
window.__brainsyncDismissSplash = (() => {
  let dismissed = false;
  let hidden = false;
  const hide = (splash) => {
    if (hidden) return;
    hidden = true;
    splash.classList.add("bs-hide");
    setTimeout(() => splash.remove(), 500);
  };
  return () => {
    if (dismissed) return;
    dismissed = true;
    const splash = document.getElementById("brainsync-splash");
    if (!splash) return;

    // Cold app launch: let the BrainSync wordmark finish its reveal once
    // before the splash goes, even if the dashboard got there first. The
    // letters stagger in over ~1.3s, so dismissing on a fast device used to
    // cut the animation off part-way and the wordmark never read as anything.
    //
    // The real durations come from the Web Animations API rather than a
    // hard-coded guess, so editing the CSS can't desync this. Only applies
    // inside the Android app (html.bs-twa) - a browser tab keeps exactly
    // today's timing. The splash element only exists on the first document
    // load, so in-app SPA navigations are unaffected by construction.
    try {
      if (
        document.documentElement.classList.contains("bs-twa") &&
        typeof splash.getAnimations === "function"
      ) {
        const running = splash
          .getAnimations({ subtree: true })
          .filter((a) => a.playState === "running");
        if (running.length) {
          Promise.all(running.map((a) => a.finished.catch(() => {}))).then(() =>
            hide(splash)
          );
          // Hard cap so a stalled animation can never strand the user.
          setTimeout(() => hide(splash), 1600);
          return;
        }
      }
    } catch (_) { /* fall through to dismissing immediately */ }

    hide(splash);
  };
})();

// Safety net: dismiss the pre-React splash if no page has called the
// helper yet. Standalone (TWA / installed PWA) cold-starts commonly
// need >1.2s to resolve the session cookie because the WebView's
// CookieStore hasn't propagated yet - if we dismiss the splash while
// Dashboard is still returning null waiting on auth, the user sees a
// jarring white-flash → logo → landing cascade on first launch. Give
// TWA a much longer budget (4s) so the splash stays put until the
// real destination (AppLanding or Dashboard) is mounted and calls
// the dismiss helper itself. Regular browser tabs still get 1.2s so
// static pages (Privacy, Terms, etc.) that forget to dismiss don't
// leave a long-running splash on top of their content.
(function scheduleSplashDismiss() {
    let budget = 1200;
    try {
        const isStandalone =
            (window.sessionStorage && window.sessionStorage.getItem('bs_is_twa') === '1')
            || window.matchMedia('(display-mode: standalone)').matches
            || window.navigator.standalone === true
            || (document.referrer || '').indexOf('android-app://') === 0;
        if (isStandalone) budget = 4000;
    } catch (_) { /* ignore */ }
    setTimeout(() => window.__brainsyncDismissSplash?.(), budget);
})();
